import {
  PARCO_CSV_HEADERS,
  PARCO_CSV_ITEM_COLUMNS,
  ParcoCsvItemColumn,
  parcoCsvStatusLabel,
} from '../../domain/parcoCsv'
import { SalesReportWithTenant } from '../../slices/services/api'
import { formatEightDigitDate, formatSlashDate } from '../../utils/date'
import { downloadBlob } from '../../utils/download'
import { safeParseNumber } from '../../utils/number'

// パルコ(013)向けCSV。1テナント1行・ヘッダー有り・金額はすべてOCR値 (Issue #3408 / Epic #3094 要件3)
//
// NOTE: 既存orgのCSV(downloadCSV.ts)は確定値ベースだが、パルコは確定前に出力するため
//       確定値が存在しない。よってOCR値を出力する。
// NOTE: 再送された画像はOCRが実行されない仕様のため、再送後に再出力しても金額は初回OCR値のまま。
//       金額の訂正はadminの確定値入力で行う運用（業務側と合意済み）。

// 日本語のヘッダー・店舗名をExcelで開く前提のためBOMを付与する
const BOM = '﻿'

// RFC4180準拠のエスケープ。店舗名・テナント名にカンマやダブルクォートが含まれ得るため必須。
const escapeCsvField = (field: string): string => {
  if (/[",\r\n]/.test(field)) {
    return `"${field.replace(/"/g, '""')}"`
  }
  return field
}

// 数式インジェクション対策。Excelは = + - @ (およびタブ・CR)で始まるセルを数式として解釈するため、
// 先頭にシングルクォートを付けて無効化する。
const escapeTextField = (field: string): string =>
  escapeCsvField(/^[=+\-@\t\r]/.test(field) ? `'${field}` : field)

// CSVの1行を組み立てる。文字列列は数式インジェクション対策込みでエスケープする。
// 金額列を number で受けるのは、自由入力値が数式対策なしのレーンに紛れ込むのと、
// 負値が '-1000 となって連携先の数値取り込みが壊れるのを、型で同時に防ぐため。
const buildCsvRow = (textFields: string[], numberFields: number[]): string =>
  [...textFields.map(escapeTextField), ...numberFields.map(String)].join(',')

// 全レジスターを横断して、指定された列に対応する算出項目のOCR値を合算する。
// 算出項目の同定は sendParameterName で行う（項目名の表記ゆれに強い。parcoCsv.ts のNOTE参照）。
const sumOcrValue = (
  salesReport: SalesReportWithTenant,
  column: ParcoCsvItemColumn
): number =>
  salesReport.registers
    .flatMap((register) => register.calculationResults)
    .filter(
      (calcResult) =>
        calcResult.calculationItem.sendParameterName ===
        column.sendParameterName
    )
    .reduce(
      (total, calcResult) =>
        total + safeParseNumber(calcResult.results?.ocrValue),
      0
    )

export const generateParcoCSVData = (
  storeCode: string,
  storeName: string,
  salesReports: SalesReportWithTenant[]
): string => {
  if (!salesReports.length) {
    return ''
  }

  const dataRows = salesReports.map((salesReport) =>
    buildCsvRow(
      [
        parcoCsvStatusLabel(salesReport.reportStatus),
        formatSlashDate(salesReport.date),
        storeCode,
        storeName,
        salesReport.tenant.code,
        salesReport.tenant.name,
      ],
      PARCO_CSV_ITEM_COLUMNS.map((column) => sumOcrValue(salesReport, column))
    )
  )

  return [PARCO_CSV_HEADERS.join(','), ...dataRows].join('\r\n')
}

export const downloadParcoCSV = (
  storeCode: string,
  storeName: string,
  date: string,
  salesReports: SalesReportWithTenant[]
): void => {
  const csvData = generateParcoCSVData(storeCode, storeName, salesReports)
  const blob = new Blob([`${BOM}${csvData}`], {
    type: 'text/csv;charset=utf-8',
  })
  downloadBlob(blob, `NIPPOU_${formatEightDigitDate(date)}.csv`)
}
